
import React, { useState, useEffect, useCallback } from 'react';
import styles from './OrderTable.module.css';
import * as XLSX from 'xlsx';
import { ordersApi } from '../../../../../api/orders/ordersApi';

const OrderTable = () => {
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const itemsPerPage = 20;

  const months = [
    { label: 'All Months', value: '' },
    { label: 'January', value: '1' },
    { label: 'February', value: '2' },
    { label: 'March', value: '3' },
    { label: 'April', value: '4' },
    { label: 'May', value: '5' },
    { label: 'June', value: '6' },
    { label: 'July', value: '7' },
    { label: 'August', value: '8' },
    { label: 'September', value: '9' },
    { label: 'October', value: '10' },
    { label: 'November', value: '11' },
    { label: 'December', value: '12' }
  ];

  const statusFilters = [
    { label: 'All', value: '' },
    { label: 'Open', value: 'Open' },
    { label: 'Closed', value: 'Closed' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  // Fetch available years on mount
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const data = await ordersApi.getOrderYears();
        if (data && Array.isArray(data)) {
          setAvailableYears(data);
        }
      } catch {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let y = currentYear; y >= 2023; y--) years.push(y.toString());
        setAvailableYears(years);
      }
    };
    fetchYears();
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await ordersApi.getOrders(
        currentPage,
        itemsPerPage,
        searchTerm,
        selectedMonth,
        selectedStatus,
        sortBy,
        sortOrder,
        selectedYear
      );

      if (data && data.orders && Array.isArray(data.orders)) {
        setOrders(data.orders);
        setTotalPages(data.totalPages || 0);
        setTotalItems(data.totalItems || 0);
      } else {
        setOrders([]);
        setError('Invalid data received from server');
      }
    } catch (err) {
      setError('Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, selectedMonth, selectedStatus, sortBy, sortOrder, selectedYear]);

  // Debounced auto-fetch on search term change
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch whenever dependencies change
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedStatus('');
    setSortBy('');
    setSortOrder('desc');
    setCurrentPage(0);
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    setCurrentPage(0);
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    setCurrentPage(0);
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    setCurrentPage(0);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(0);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const data = await ordersApi.exportOrders(
        searchTerm, selectedMonth, selectedStatus, selectedYear
      );

      if (!data || !Array.isArray(data) || data.length === 0) {
        alert('No data to export');
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(data.map(order => ({
        'Document Status': order.documentStatus,
        'SO Number': order.soNumber,
        'Posting Date': order.postingDate,
        'Customer PO No': order.customerPoNo,
        'PO Date': order.poDate,
        'Customer Name': order.customerName,
        'Sales Employee Name': order.salesEmployeeName,
        'Contact Person': order.contactPerson,
        'Item Code': order.itemCode,
        'Item Name': order.itemName,
        'CAS No': order.casNo,
        'Item Group': order.itemGroupName,
        'Mfr Catalog No.': order.mfrCatalogNo,
        'Pack Size': order.packSize,
        'UOM': order.uom,
        'Quantity Order': order.quantityOrder,
        'Open Qty': order.openQty,
        'Delivered Quantity': order.deliveredQuantity,
        'Stock Status - In Hyd': order.stockStatus,
        'Delivery Date': order.deliveryDate,
        'Timeline': order.timeline,
        'Mkt_Feedback': order.mktFeedback,
        'Price': order.price,
        'Open Amount': order.openAmount,
        'Line Total': order.lineTotal,
        'Discount (%)': order.discountPercent,
        'Tax Code': order.taxCode,
        'Unit': order.unit,
        'Invoice No': order.invoiceNo,
        'Invoice Date': order.invoiceDate,
        'Tracking No': order.trackingNo,
        'Dispatch Date': order.dispatchDate,
        'Invoice Delivery Date': order.invoiceDeliveryDate,
        'Batch No': order.batchNo,
      })));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'All Orders');
      XLSX.writeFile(workbook, `all_orders_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch {
      alert('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return '₹' + (amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '⇅';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const hasActiveFilters = searchTerm || selectedYear || selectedMonth || selectedStatus;

  return (
    <div className={styles.wrapper}>
      <div className={styles.filtersSection}>
        {/* Search - no button, auto-fetch on typing */}
        <div className={styles.searchRow}>
          <div className={styles.searchInputWrapper}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              type="text"
              placeholder="Search by SO#, Customer, Item, Invoice#, Tracking#..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className={styles.clearSearchButton} title="Clear search">
                ✕
              </button>
            )}
          </div>
        </div>

        <div className={styles.filterRow}>
          <div className={styles.filterGroupRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(e.target.value)}
                className={styles.filterSelect}
                disabled={loading}
              >
                <option value="">All Years</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Month:</label>
              <select
                value={selectedMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
                className={styles.filterSelect}
                disabled={loading}
              >
                {months.map((month) => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>

            <div className={styles.statusButtonGroup}>
              {statusFilters.map((filter) => (
                <button
                  key={filter.value}
                  className={`${styles.statusButton} ${selectedStatus === filter.value ? styles.statusButtonActive : ''}`}
                  onClick={() => handleStatusChange(filter.value)}
                  disabled={loading}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.actionButtons}>
            <button onClick={handleClearFilters} disabled={loading} className={styles.clearButton}>
              Clear All Filters
            </button>
            <button onClick={handleExportExcel} disabled={loading || totalItems === 0} className={styles.excelButton}>
              <span className={styles.excelIcon}>📊</span>
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {hasActiveFilters && (
        <div className={styles.activeFilters}>
          <span className={styles.activeFiltersLabel}>Active Filters:</span>
          {searchTerm && (
            <div className={styles.filterTag}>
              Search: "{searchTerm}"
              <button onClick={() => setSearchTerm('')} className={styles.removeFilterButton}>✕</button>
            </div>
          )}
          {selectedYear && (
            <div className={styles.filterTag}>
              Year: {selectedYear}
              <button onClick={() => handleYearChange('')} className={styles.removeFilterButton}>✕</button>
            </div>
          )}
          {selectedMonth && (
            <div className={styles.filterTag}>
              Month: {months.find(m => m.value === selectedMonth)?.label}
              <button onClick={() => handleMonthChange('')} className={styles.removeFilterButton}>✕</button>
            </div>
          )}
          {selectedStatus && (
            <div className={styles.filterTag}>
              Status: {selectedStatus}
              <button onClick={() => handleStatusChange('')} className={styles.removeFilterButton}>✕</button>
            </div>
          )}
        </div>
      )}

      {error && <div className={styles.errorMessage}>{error}</div>}

      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <div className={styles.loadingText}>Loading orders...</div>
        </div>
      )}

      {!loading && (
        <div className={styles.tableContainer}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th} onClick={() => handleSort('documentStatus')}>
                    Status {getSortIcon('documentStatus')}
                  </th>
                  <th className={styles.th} onClick={() => handleSort('soNumber')}>
                    SO# {getSortIcon('soNumber')}
                  </th>
                  <th className={styles.th} onClick={() => handleSort('postingDate')}>
                    Posting Date {getSortIcon('postingDate')}
                  </th>
                  <th className={styles.th}>Customer PO No</th>
                  <th className={styles.th}>PO Date</th>
                  <th className={styles.th}>Customer Name</th>
                  <th className={styles.th}>Sales Person</th>
                  <th className={styles.th}>Contact Person</th>
                  <th className={styles.th}>Item Code</th>
                  <th className={styles.th}>Item Name</th>
                  <th className={styles.th}>CAS No</th>
                  <th className={styles.th}>Item Group</th>
                  <th className={styles.th}>Mfr Catalog No</th>
                  <th className={styles.th}>Pack Size</th>
                  <th className={styles.th}>UOM</th>
                  <th className={styles.th} onClick={() => handleSort('quantityOrder')}>
                    Qty Order {getSortIcon('quantityOrder')}
                  </th>
                  <th className={styles.th}>Open Qty</th>
                  <th className={styles.th}>Delivered Qty</th>
                  <th className={styles.th}>Stock Status - In Hyd</th>
                  <th className={styles.th}>Delivery Date</th>
                  <th className={styles.th}>Timeline</th>
                  <th className={styles.th}>Mkt Feedback</th>
                  <th className={styles.th} onClick={() => handleSort('price')}>
                    Price {getSortIcon('price')}
                  </th>
                  <th className={styles.th} onClick={() => handleSort('openAmount')}>
                    Open Amount {getSortIcon('openAmount')}
                  </th>
                  <th className={styles.th}>Line Total</th>
                  <th className={styles.th}>Discount %</th>
                  <th className={styles.th}>Tax Code</th>
                  <th className={styles.th}>Unit</th>
                  <th className={styles.th}>Invoice No</th>
                  <th className={styles.th}>Invoice Date</th>
                  <th className={styles.th}>Tracking No</th>
                  <th className={styles.th}>Dispatch Date</th>
                  <th className={styles.th}>Invoice Delivery Date</th>
                  <th className={styles.th}>Batch No</th>
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map((order, index) => (
                    <tr key={`${order.soNumber}-${order.lineNum}-${index}`} className={styles.tr}>
                      <td className={styles.td}>
                        <span className={`${styles.statusBadge} ${styles[`status${order.documentStatus}`]}`}>
                          {order.documentStatus}
                        </span>
                      </td>
                      <td className={styles.td}>{order.soNumber}</td>
                      <td className={styles.td}>{formatDate(order.postingDate)}</td>
                      <td className={styles.td}><div className={styles.truncate}>{order.customerPoNo || 'N/A'}</div></td>
                      <td className={styles.td}>{formatDate(order.poDate)}</td>
                      <td className={styles.td}><div className={styles.truncate}>{order.customerName}</div></td>
                      <td className={styles.td}><div className={styles.truncate}>{order.salesEmployeeName || 'N/A'}</div></td>
                      <td className={styles.td}><div className={styles.truncate}>{order.contactPerson || 'N/A'}</div></td>
                      <td className={styles.td}><div className={styles.truncate}>{order.itemCode}</div></td>
                      <td className={styles.td}><div className={styles.truncate}>{order.itemName}</div></td>
                      <td className={styles.td}><div className={styles.truncate}>{order.casNo || 'N/A'}</div></td>
                      <td className={styles.td}><div className={styles.truncate}>{order.itemGroupName || 'N/A'}</div></td>
                      <td className={styles.td}><div className={styles.truncate}>{order.mfrCatalogNo || 'N/A'}</div></td>
                      <td className={styles.td}>{order.packSize || 0}</td>
                      <td className={styles.td}>{order.uom || 'N/A'}</td>
                      <td className={styles.td}>{order.quantityOrder?.toFixed(2) || '0.00'}</td>
                      <td className={styles.td}>{order.openQty?.toFixed(2) || '0.00'}</td>
                      <td className={styles.td}>{order.deliveredQuantity?.toFixed(2) || '0.00'}</td>
                      <td className={styles.td}>
                        <span className={`${styles.stockBadge} ${order.stockStatus === 'In Stock' ? styles.stockInStock : styles.stockOutOfStock}`}>
                          {order.stockStatus}
                        </span>
                      </td>
                      <td className={styles.td}>{formatDate(order.deliveryDate)}</td>
                      <td className={styles.td}><div className={styles.truncate}>{order.timeline || 'N/A'}</div></td>
                      <td className={styles.td}><div className={styles.truncate}>{order.mktFeedback || 'N/A'}</div></td>
                      <td className={styles.td}>{formatCurrency(order.price)}</td>
                      <td className={styles.td}>{formatCurrency(order.openAmount)}</td>
                      <td className={styles.td}>{formatCurrency(order.lineTotal)}</td>
                      <td className={styles.td}>{order.discountPercent?.toFixed(2) || '0.00'}%</td>
                      <td className={styles.td}>{order.taxCode || 'N/A'}</td>
                      <td className={styles.td}>{order.unit || 'N/A'}</td>
                      <td className={styles.td}>{order.invoiceNo || 'N/A'}</td>
                      <td className={styles.td}>{formatDate(order.invoiceDate)}</td>
                      <td className={styles.td}><div className={styles.truncate}>{order.trackingNo || 'N/A'}</div></td>
                      <td className={styles.td}>{formatDate(order.dispatchDate)}</td>
                      <td className={styles.td}>{formatDate(order.invoiceDeliveryDate)}</td>
                      <td className={styles.td}><div className={styles.truncate}>{order.batchNo || 'N/A'}</div></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className={styles.td} colSpan="34" style={{ textAlign: 'center', padding: '40px' }}>
                      {hasActiveFilters ? 'No orders match your filters' : 'No orders found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && orders.length > 0 && (
        <div className={styles.paginationSection}>
          <div className={styles.paginationInfo}>
            Showing {currentPage * itemsPerPage + 1} - {Math.min((currentPage + 1) * itemsPerPage, totalItems)} of {totalItems} records
          </div>
          <div className={styles.paginationControls}>
            <button onClick={() => setCurrentPage(0)} disabled={currentPage === 0} className={styles.paginationButton}>First</button>
            <button onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} className={styles.paginationButton}>Prev</button>
            <div className={styles.pageNumber}>{currentPage + 1} / {totalPages}</div>
            <button onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1} className={styles.paginationButton}>Next</button>
            <button onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage === totalPages - 1} className={styles.paginationButton}>Last</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTable;